import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Star, XCircle } from "lucide-react";
import PaymentMarquee from "@/components/PaymentMarquee";
import SEO from "@/components/SEO";

const SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };

const plans = [
  {
    key: "starter",
    name: "Starter",
    tag: "15 AH TIPS",
    price: 190,
    currency: "EUR",
    highlight: false,
    unavailable: true,
    features: [
      "15 Asian Handicap selections",
      "Delivered via email, on time before kickoff",
      "Recommended stake for each pick",
      "Full match reasoning included",
      "Access to private archive",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    tag: "30 AH TIPS",
    price: 290,
    currency: "EUR",
    highlight: false,
    unavailable: true,
    features: [
      "30 Asian Handicap selections",
      "Priority delivery (early lines)",
      "Recommended stake for each pick",
      "Full match reasoning + line-shopping notes",
      "Access to private archive",
      "Bankroll management guidance",
    ],
  },
  {
    key: "vip",
    name: "VIP",
    tag: "50 AH TIPS",
    price: 590,
    currency: "EUR",
    highlight: false,
    unavailable: false,
    features: [
      "50 Asian Handicap selections",
      "Priority delivery (early lines)",
      "Recommended stake for each pick",
      "Full match reasoning + line-shopping notes",
      "Access to private archive",
      "Bankroll management guidance",
      "Direct chat access to our tipsters",
      "Replacement match for every losing match",
    ],
  },
];

export default function Pricing() {
  const [live, setLive] = useState(plans);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`)
      .then(({ data }) => {
        const byKey = Object.fromEntries(data.plans.map((p) => [p.key, p]));
        setLive(plans.map((p) => ({ ...p, ...(byKey[p.key] || {}) })));
      })
      .catch(() => {});
  }, []);

  const fmt = (p) => `${SYMBOLS[p.currency] || p.currency}${p.price}`;
  const seoPrices = live.map((p) => `${p.tag.split(" ")[0]} AH Tips ${fmt(p)}`).join(", ");

  return (
    <div data-testid="pricing-page">
      <SEO
        title="Subscription Plans — 15, 30 & 50 Asian Handicap Tips"
        description={`Simple, flat-fee Asian Handicap tip packages. ${seoPrices}. No recurring charges. Payments via Skrill, Neteller, PayPal, Crypto, Revolut and more.`}
        path="/pricing"
        keywords="Asian Handicap subscription, AH tips price, football tips package, buy AH predictions, Skrill football tips, crypto football tips"
      />
      <section className="bg-ink-950 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-emerald-700/25 blur-[120px]" />
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-gold-500/15 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold-400 font-mono uppercase tracking-widest text-xs mb-3">
            Subscription Plans
          </div>
          <h1
            data-testid="pricing-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"
          >
            Simple <span className="gold-text">flat-fee</span> packages. No monthly lock-in.
          </h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
            Pay for the number of Asian Handicap tips you want to receive.
            When the tips are delivered and settled, the package is done — no
            recurring charges.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-ink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="pricing-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
          >
            {live.map((p) => (
              <div
                key={p.name}
                data-testid={`plan-${p.name.toLowerCase()}`}
                className={`relative p-8 sm:p-10 rounded-3xl bg-white border transition-all ${
                  p.highlight
                    ? "border-2 border-gold-500 shadow-2xl shadow-gold-500/10 md:scale-[1.04] md:-translate-y-2 z-10"
                    : p.unavailable
                    ? "border-ink-200 opacity-70"
                    : "border-ink-200 hover:shadow-xl hover:shadow-ink-200/50"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gold-500 text-ink-950 text-xs font-bold uppercase tracking-widest shadow-lg">
                    <Star className="w-3.5 h-3.5" fill="currentColor" />
                    Most Popular
                  </div>
                )}
                {p.unavailable && (
                  <div
                    data-testid={`plan-${p.name.toLowerCase()}-unavailable`}
                    className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold uppercase tracking-widest shadow-lg whitespace-nowrap"
                  >
                    <XCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
                    Not Available
                  </div>
                )}

                <div className="text-emerald-700 font-mono uppercase tracking-widest text-[11px]">
                  {p.tag}
                </div>
                <h3 className="mt-2 font-display font-bold text-3xl text-ink-900">
                  {p.name}
                </h3>

                <div className="mt-6 flex items-baseline gap-1.5">
                  <span
                    data-testid={`plan-${p.key}-price`}
                    className="font-display font-bold text-5xl sm:text-6xl text-ink-900"
                  >
                    {fmt(p)}
                  </span>
                  <span className="text-ink-500 text-sm font-medium">{p.currency}</span>
                </div>
                <p className="text-xs text-ink-500 mt-1 font-mono uppercase tracking-wider">
                  One-time payment
                </p>

                <div className="my-8 h-px bg-ink-200" />

                <ul className="space-y-3.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-ink-700">
                      <div className="w-5 h-5 rounded-full bg-emerald-700/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-emerald-700" strokeWidth={3} />
                      </div>
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-9 pt-6 border-t border-ink-200">
                  <div className="text-xs text-ink-500 font-mono uppercase tracking-wider">
                    To subscribe, contact us via the{" "}
                    <a
                      href="/contact"
                      className="text-emerald-700 hover:text-gold-600 underline"
                    >
                      Contact page
                    </a>
                    .
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Payments */}
          <div className="mt-20 sm:mt-24">
            <div className="text-center mb-8">
              <div className="text-emerald-700 font-mono uppercase tracking-widest text-xs mb-2">
                Accepted Payment Methods
              </div>
              <h3 className="font-display font-semibold text-2xl sm:text-3xl text-ink-900">
                Pay how you prefer
              </h3>
            </div>
            <div className="bg-white rounded-2xl border border-ink-200 py-2 shadow-sm">
              <PaymentMarquee />
            </div>
          </div>

          <div
            data-testid="pricing-fineprint"
            className="mt-14 max-w-3xl mx-auto p-6 rounded-xl bg-white border border-ink-200 text-sm text-ink-700 leading-relaxed"
          >
            <p className="font-semibold text-ink-900 mb-2">A note on our pricing model:</p>
            <p>
              Our analysts identify strong statistical edges daily, and our
              customers receive carefully selected opportunities directly when
              they are available. This model allows us to provide consistent
              value while maintaining the high standards and performance that
              our subscribers trust.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
